import { addDays, addHours, addMinutes, addMonths, addYears, isAfter, isBefore, set, } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { resolveIntervalDays } from './interval-days.js';
import { resolveDaysOfMonthFromConfig } from './month-days.js';
const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export function resolveStartDateLocal(startDate, timezone, startDateLocal) {
    if (startDateLocal && LOCAL_DATE_PATTERN.test(startDateLocal)) {
        return startDateLocal;
    }
    return formatInTimeZone(startDate, timezone, 'yyyy-MM-dd');
}
export function applyLocalDateTime(dateLocal, time, timezone) {
    const { hours, minutes } = parseTime(time);
    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    return fromZonedTime(`${dateLocal}T${hh}:${mm}:00`, timezone);
}
function parseTime(time) {
    const [hours, minutes] = time.split(':').map(Number);
    return { hours: hours ?? 0, minutes: minutes ?? 0 };
}
function applyTimeInTimezone(date, time, timezone) {
    const { hours, minutes } = parseTime(time);
    const zoned = toZonedTime(date, timezone);
    const withTime = set(zoned, {
        hours,
        minutes,
        seconds: 0,
        milliseconds: 0,
    });
    return fromZonedTime(withTime, timezone);
}
function isWithinActivePeriod(date, startDate, endDate) {
    if (isBefore(date, startDate)) {
        return false;
    }
    if (endDate && isAfter(date, endDate)) {
        return false;
    }
    return true;
}
function getDayOfWeekInTimezone(date, timezone) {
    const zoned = toZonedTime(date, timezone);
    return zoned.getDay();
}
function matchesCycleMonth(month1Based, anchorMonth, cycleMonths) {
    return (month1Based - anchorMonth + 12) % cycleMonths === 0;
}
function matchesDayOfMonth(zoned, daysOfMonth) {
    return daysOfMonth.includes(zoned.getDate());
}
function findNextDaysOfMonthRun(candidate, referenceDate, startDate, endDate, timezone, time, daysOfMonth, lastRunAt) {
    let searchFrom = lastRunAt ? addDays(candidate, 1) : candidate;
    for (let i = 0; i < 366; i += 1) {
        const zoned = toZonedTime(searchFrom, timezone);
        if (matchesDayOfMonth(zoned, daysOfMonth)) {
            const run = applyTimeInTimezone(searchFrom, time, timezone);
            if (isAfter(run, referenceDate) && isWithinActivePeriod(run, startDate, endDate)) {
                return run;
            }
        }
        searchFrom = addDays(searchFrom, 1);
    }
    return null;
}
function findNextCycleMonthRun(candidate, referenceDate, startDate, endDate, timezone, time, anchorMonth, dayOfMonth, cycleMonths, lastRunAt) {
    let searchFrom = lastRunAt ? addMonths(candidate, cycleMonths) : candidate;
    for (let i = 0; i < 48; i += 1) {
        const zoned = toZonedTime(searchFrom, timezone);
        const month = zoned.getMonth() + 1;
        if (!matchesCycleMonth(month, anchorMonth, cycleMonths)) {
            searchFrom = addMonths(searchFrom, 1);
            continue;
        }
        const cycleZoned = set(zoned, {
            date: Math.min(dayOfMonth, 28),
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
        });
        const cycleRun = applyTimeInTimezone(fromZonedTime(cycleZoned, timezone), time, timezone);
        if (isAfter(cycleRun, referenceDate) && isWithinActivePeriod(cycleRun, startDate, endDate)) {
            return cycleRun;
        }
        searchFrom = addMonths(searchFrom, cycleMonths);
    }
    return null;
}
export function calculateNextRun(input) {
    const { scheduleType, scheduleConfig, timezone, startDate, endDate, lastRunAt, referenceDate = new Date(), } = input;
    const time = scheduleConfig.time ?? '09:00';
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time) ||
        [startDate, referenceDate, endDate, lastRunAt].some((date) => date && Number.isNaN(date.getTime()))) {
        return { nextRunAt: null, reason: 'Invalid schedule' };
    }
    try {
        new Intl.DateTimeFormat('en', { timeZone: timezone }).format(referenceDate);
    }
    catch {
        return { nextRunAt: null, reason: 'Invalid schedule' };
    }
    let candidate;
    if (lastRunAt) {
        candidate = new Date(lastRunAt);
    }
    else if (isAfter(referenceDate, startDate)) {
        candidate = new Date(referenceDate);
    }
    else {
        candidate = new Date(startDate);
    }
    const maxIterations = 366 * 5;
    let iterations = 0;
    while (iterations < maxIterations) {
        iterations += 1;
        switch (scheduleType) {
            case 'once': {
                const dateLocal = resolveStartDateLocal(startDate, timezone, input.startDateLocal);
                const onceRun = applyLocalDateTime(dateLocal, time, timezone);
                if ((lastRunAt && !isBefore(lastRunAt, onceRun)) || !isAfter(onceRun, referenceDate)) {
                    return { nextRunAt: null, reason: 'One-time reminder already executed or passed' };
                }
                if (endDate && isAfter(onceRun, endDate)) {
                    return { nextRunAt: null, reason: 'Outside active period' };
                }
                return { nextRunAt: onceRun };
            }
            case 'daily': {
                // Advance from lastRunAt only on the first iteration; later iterations
                // already moved candidate forward in the break below.
                if (lastRunAt && iterations === 1) {
                    candidate = addDays(candidate, 1);
                }
                const dailyRun = applyTimeInTimezone(candidate, time, timezone);
                if (isAfter(dailyRun, referenceDate) &&
                    isWithinActivePeriod(dailyRun, startDate, endDate)) {
                    return { nextRunAt: dailyRun };
                }
                candidate = addDays(candidate, 1);
                break;
            }
            case 'weekly': {
                const daysOfWeek = scheduleConfig.daysOfWeek ?? [1];
                let searchFrom = lastRunAt ? addDays(candidate, 1) : candidate;
                for (let i = 0; i < 14; i += 1) {
                    const day = getDayOfWeekInTimezone(searchFrom, timezone);
                    if (daysOfWeek.includes(day)) {
                        const weeklyRun = applyTimeInTimezone(searchFrom, time, timezone);
                        if (isAfter(weeklyRun, referenceDate) &&
                            isWithinActivePeriod(weeklyRun, startDate, endDate)) {
                            return { nextRunAt: weeklyRun };
                        }
                    }
                    searchFrom = addDays(searchFrom, 1);
                }
                candidate = addDays(candidate, 7);
                break;
            }
            case 'monthly': {
                const daysOfMonth = resolveDaysOfMonthFromConfig(scheduleConfig);
                const nextRunAt = findNextDaysOfMonthRun(candidate, referenceDate, startDate, endDate, timezone, time, daysOfMonth, lastRunAt);
                if (nextRunAt) {
                    return { nextRunAt };
                }
                return { nextRunAt: null, reason: 'Could not find next monthly run' };
            }
            case 'bimonthly': {
                const dayOfMonth = scheduleConfig.dayOfMonth ?? 1;
                const anchorMonth = scheduleConfig.month ?? toZonedTime(startDate, timezone).getMonth() + 1;
                const nextRunAt = findNextCycleMonthRun(candidate, referenceDate, startDate, endDate, timezone, time, anchorMonth, dayOfMonth, 2, lastRunAt);
                if (nextRunAt) {
                    return { nextRunAt };
                }
                return { nextRunAt: null, reason: 'Could not find next bimonthly run' };
            }
            case 'quarterly': {
                const dayOfMonth = scheduleConfig.dayOfMonth ?? 1;
                const anchorMonth = scheduleConfig.month ?? toZonedTime(startDate, timezone).getMonth() + 1;
                const nextRunAt = findNextCycleMonthRun(candidate, referenceDate, startDate, endDate, timezone, time, anchorMonth, dayOfMonth, 3, lastRunAt);
                if (nextRunAt) {
                    return { nextRunAt };
                }
                return { nextRunAt: null, reason: 'Could not find next quarterly run' };
            }
            case 'semiannual': {
                const dayOfMonth = scheduleConfig.dayOfMonth ?? 1;
                const anchorMonth = scheduleConfig.month ?? toZonedTime(startDate, timezone).getMonth() + 1;
                const nextRunAt = findNextCycleMonthRun(candidate, referenceDate, startDate, endDate, timezone, time, anchorMonth, dayOfMonth, 6, lastRunAt);
                if (nextRunAt) {
                    return { nextRunAt };
                }
                return { nextRunAt: null, reason: 'Could not find next semiannual run' };
            }
            case 'yearly': {
                const month = (scheduleConfig.month ?? 1) - 1;
                const day = scheduleConfig.day ?? 1;
                candidate = lastRunAt ? addYears(candidate, 1) : candidate;
                const zoned = toZonedTime(candidate, timezone);
                const yearlyZoned = set(zoned, {
                    month,
                    date: day,
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                    milliseconds: 0,
                });
                const yearlyRun = applyTimeInTimezone(fromZonedTime(yearlyZoned, timezone), time, timezone);
                if (isAfter(yearlyRun, referenceDate) &&
                    isWithinActivePeriod(yearlyRun, startDate, endDate)) {
                    return { nextRunAt: yearlyRun };
                }
                candidate = addYears(candidate, 1);
                break;
            }
            case 'interval': {
                const unit = scheduleConfig.intervalUnit ?? 'day';
                const dayInterval = resolveIntervalDays(scheduleConfig.intervalValue ?? scheduleConfig.intervalDays);
                if (unit === 'month') {
                    const dayOfMonth = scheduleConfig.dayOfMonth ?? 1;
                    const anchorMonth = scheduleConfig.month ?? toZonedTime(startDate, timezone).getMonth() + 1;
                    const cycleMonths = scheduleConfig.intervalValue ?? 1;
                    const nextRunAt = findNextCycleMonthRun(candidate, referenceDate, startDate, endDate, timezone, time, anchorMonth, dayOfMonth, cycleMonths, lastRunAt);
                    if (nextRunAt) {
                        return { nextRunAt };
                    }
                    return { nextRunAt: null, reason: 'Could not find next monthly interval run' };
                }
                if (unit === 'year') {
                    const month = (scheduleConfig.month ?? 1) - 1;
                    const day = scheduleConfig.day ?? scheduleConfig.dayOfMonth ?? 1;
                    const yearInterval = scheduleConfig.intervalValue ?? 1;
                    candidate = lastRunAt ? addYears(candidate, yearInterval) : candidate;
                    const zoned = toZonedTime(candidate, timezone);
                    const yearlyZoned = set(zoned, {
                        month,
                        date: day,
                        hours: 0,
                        minutes: 0,
                        seconds: 0,
                        milliseconds: 0,
                    });
                    const yearlyRun = applyTimeInTimezone(fromZonedTime(yearlyZoned, timezone), time, timezone);
                    if (isAfter(yearlyRun, referenceDate) &&
                        isWithinActivePeriod(yearlyRun, startDate, endDate)) {
                        return { nextRunAt: yearlyRun };
                    }
                    candidate = addYears(candidate, yearInterval);
                    break;
                }
                if (unit === 'minute' || unit === 'hour') {
                    const intervalValue = scheduleConfig.intervalValue ?? 1;
                    const step = (date) => unit === 'minute' ? addMinutes(date, intervalValue) : addHours(date, intervalValue);
                    let next = lastRunAt ? step(lastRunAt) : applyTimeInTimezone(startDate, time, timezone);
                    for (let i = 0; i < 10_000; i += 1) {
                        if (isAfter(next, referenceDate) && isWithinActivePeriod(next, startDate, endDate)) {
                            return { nextRunAt: next };
                        }
                        if (endDate && isAfter(next, endDate)) {
                            return { nextRunAt: null, reason: 'Outside active period' };
                        }
                        next = step(next);
                    }
                    return { nextRunAt: null, reason: 'Could not find next sub-daily interval run' };
                }
                const intervalDays = unit === 'week' ? dayInterval * 7 : dayInterval;
                candidate = lastRunAt ? addDays(candidate, intervalDays) : candidate;
                const intervalRun = applyTimeInTimezone(candidate, time, timezone);
                if (isAfter(intervalRun, referenceDate) &&
                    isWithinActivePeriod(intervalRun, startDate, endDate)) {
                    return { nextRunAt: intervalRun };
                }
                candidate = addDays(candidate, intervalDays);
                break;
            }
            default: {
                return {
                    nextRunAt: null,
                    reason: `Unknown schedule type: ${String(scheduleType)}`,
                };
            }
        }
    }
    return { nextRunAt: null, reason: 'Could not find next run within search window' };
}
//# sourceMappingURL=calculator.js.map